import type { RepositoryError } from '@application/ports';
import { getServices } from '@infrastructure/services';

/**
 * The error a screen throws when a read fails, with the reason still attached.
 *
 * Thirteen screens used to do this instead:
 *
 * ```ts
 * if (!result.ok) throw new Error('No pudimos cargar los permisos.');
 * ```
 *
 * which is a sentence about the screen and says nothing about what happened. The
 * adapter had already built the part that distinguishes a missing composite index
 * from a rule that refused from a network that dropped — and it was discarded on
 * the line that reported the failure. Diagnosing any of them then meant reading
 * source code and guessing, and it cost several rounds of exactly that.
 *
 * So the reason goes two places. To the console, where whoever is debugging can
 * read it in full, and — trimmed — into the message itself, because the person
 * looking at the failure is usually an employee on their own machine who is never
 * going to open a developer console, and a screenshot has to be enough.
 */

const REASON_LIMIT = 140;

export const loadFailed = (message: string, error: RepositoryError): Error => {
  getServices().logger.error(message, { error });

  const reason = 'detail' in error ? error.detail : error.what;
  if (reason.length === 0) return new Error(message);

  const trimmed =
    reason.length > REASON_LIMIT ? `${reason.slice(0, REASON_LIMIT)}…` : reason;
  return new Error(`${message} · ${trimmed}`);
};
